import React, { useMemo, useState, useEffect } from 'react';
import { AddOutlined } from '@mui/icons-material';
import { Grid } from '@mui/material';
import get from 'lodash/get';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useGetDashboard } from 'api/endpoints/getDashboard';
import { selectDisplayName } from 'features/logon/logonSlice';
import navigationPaths from 'engine/navigationPaths';

import DashboardLayout from 'components/dashboardLayout/dashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { Tile, Chart, Calendar, Documents } from './components';

import * as Styled from './Dashboard.styles';
import { AddDocumentButton } from 'components/AddDocumentButton/AddDocumentButton';
import { Subscribe, Unsubscribe, Event } from 'utility/Events/Events';

dayjs.extend(utc);

export const Dashboard = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(dayjs().utc(true));
  const {
    data: dashboardData,
    refetch: refetchDashboardData,
    isLoading: isLoadingDashboardData,
  } = useGetDashboard(currentDate);

  const displayName = useSelector(selectDisplayName);

  const revenuePerYear = useMemo(() => {
    return get(dashboardData, 'revenuePerYear', {});
  }, [dashboardData]);
  const costsPerYear = useMemo(() => {
    return get(dashboardData, 'costsPerYear', {});
  }, [dashboardData]);
  const balanceLoss = useMemo(() => {
    return get(dashboardData, 'balanceLoss', {});
  }, [dashboardData]);
  const chartData = useMemo(() => {
    return get(dashboardData, 'chartData', undefined);
  }, [dashboardData]);
  const recentDocuments = useMemo(() => {
    return get(dashboardData, 'recentDocuments', []);
  }, [dashboardData]);
  const periodicEvents = useMemo(() => {
    return get(dashboardData, 'periodicEvents', []);
  }, [dashboardData]);

  useEffect(() => {
    Subscribe(Event.onCostListNeedsRefresh, refetchDashboardData);

    return () => {
      Unsubscribe(Event.onCostListNeedsRefresh, refetchDashboardData);
    };
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Styled.Wrapper>
        <Styled.Title>
          Witaj, {displayName}
          <AddDocumentButton
            className="addInvoiceButton"
            onClick={() => navigate(navigationPaths().InvoicesForm, { state: { mode: 'create' } })}
          >
            <AddOutlined /> Dodaj fakturę
          </AddDocumentButton>
        </Styled.Title>

        <Grid container spacing={2}>
          <Grid item xs={12} lg={5} xxl={4}>
            <Styled.Box>
              <Calendar events={periodicEvents} />
            </Styled.Box>
          </Grid>
          <Grid item xs={12} lg={7} xxl={8}>
            <Styled.Box>
              <Chart chartData={chartData} />
            </Styled.Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Styled.Box>
              <Tile
                value={revenuePerYear.value}
                percent={revenuePerYear.yearOverYearPercentage}
                title="Przychód rocznie"
                year={revenuePerYear.year}
              />
            </Styled.Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Styled.Box>
              <Tile
                value={costsPerYear.value}
                percent={costsPerYear.yearOverYearPercentage}
                title="Koszty rocznie"
                year={costsPerYear.year}
              />
            </Styled.Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Styled.Box>
              <Tile
                value={balanceLoss.value}
                percent={balanceLoss.yearOverYearPercentage}
                title="Bilans/Strata"
                year={balanceLoss.year}
              />
            </Styled.Box>
          </Grid>
          <Grid item xs={12}>
            <Styled.Box>
              <Documents data={recentDocuments} />
            </Styled.Box>
          </Grid>
        </Grid>
      </Styled.Wrapper>
    </DashboardLayout>
  );
};
